import { ExternalLink } from 'lucide-react'
import { DECISIONS } from './decisions.js'
import { nameInitial } from '../lib/text.js'

/**
 * Open a patient — the ONE way (decisions #4 and #6). Audit: 5 implementations,
 * 3 destination tabs, and patient names that were plain text in some tables.
 *
 * Where it lands is set by WHERE it is clicked from, not per call site:
 *   context  'default' → การรักษา   'appointment' → นัดหมาย   'lab' → Lab
 *            'chart' · 'followup' · 'promotion' · 'info'   (DECISIONS.patientTabByContext)
 *   tab      overrides context with an explicit path ('chart/history/123')
 *
 * Same guards as components/PatientLink.jsx (its header explains each): a real
 * <a> in a NEW tab (⌘-click, middle-click, copy-link work; the list the user came
 * from stays put), stopPropagation so a row click does not also fire, plain text
 * when there is no patient (id 0 / null), not draggable as a link (HN gets
 * click-dragged to copy).
 */
export function patientHref(patientId, { context = 'default', tab } = {}) {
  const pid = Number(patientId) > 0 ? Number(patientId) : 0
  if (!pid) return ''
  const t = tab || DECISIONS.patientTabByContext[context] || DECISIONS.patientTabByContext.default
  return `/patient/${pid}/${t}`
}

export function PatientLink({ patientId, context = 'default', tab, onOpen, icon = false, className = '', children }) {
  const href = patientHref(patientId, { context, tab })
  if (!href) return <>{children}</>
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      draggable={false}
      onClick={(e) => { e.stopPropagation(); onOpen?.() }}
      title="เปิดหน้าคนไข้ในแท็บใหม่"
      className={`text-brand-700 hover:text-brand-800 hover:underline underline-offset-2 ${className}`}
    >
      {children}
      {icon && <ExternalLink size={11} className="inline ml-0.5 align-baseline opacity-60" />}
    </a>
  )
}

/**
 * The patient cell for tables and cards: "ชื่อ นามสกุล" with the HN under it,
 * BOTH clickable (decision #4 — a patient opens from name / surname / HN, never
 * from the row, which keeps its own Drawer / detail action).
 *   patient  { id, name, surname, prefix?, HN|hn }
 *   inline   one line: "ชื่อ นามสกุล · HN 12345"
 *   nickname shown muted after the name: "ชื่อ นามสกุล (ฝน)"
 *   badge    a node after the name (a <Badge>, e.g. "Warm Case")
 *   meta     a node after the HN on the second line: "HN 12345 · 34 ปี · อารีย์" — so a
 *            wide table can fold its small columns into the patient cell (2026-10-29,
 *            the implant call list went from 12 columns to 5)
 *   avatar   a round initial before the two lines — the first Thai consonant via
 *            nameInitial(), never name[0] (a vowel or tone mark would show)
 */
export function PatientName({ patient, context = 'default', tab, onOpen, inline = false, nickname, badge, meta, avatar = false }) {
  const p = patient || {}
  const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ') || p.fullname || '—'
  const hn = p.HN ?? p.hn
  const link = (child, cls) => (
    <PatientLink patientId={p.id ?? p.patient_id} context={context} tab={tab} onOpen={onOpen} className={cls}>{child}</PatientLink>
  )
  if (inline) {
    return (
      <span className="min-w-0">
        {link(name, 'font-medium')}
        {hn ? <span className="text-slate-400"> · {link(`HN ${hn}`, 'tabular-nums text-slate-500 hover:text-brand-700')}</span> : null}
      </span>
    )
  }
  const lines = (
    <span className="block min-w-0">
      <span className="block truncate">
        {link(name, 'font-medium')}
        {nickname ? <span className="text-slate-400"> ({nickname})</span> : null}
        {badge ? <span className="ml-1.5 align-middle">{badge}</span> : null}
      </span>
      {hn || meta ? (
        <span className="block text-[12px] text-slate-400">
          {hn ? link(`HN ${hn}`, 'tabular-nums text-slate-500 hover:text-brand-700') : null}
          {hn && meta ? ' · ' : null}{meta}
        </span>
      ) : null}
    </span>
  )
  if (!avatar) return lines
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[12px] font-semibold text-brand-600">
        {nameInitial(p.name || name)}
      </span>
      {lines}
    </span>
  )
}
